// src/components/BoardPackConfiguration/TypeSpecificFields.jsx
import React, { useState, useEffect, useRef, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Plus, Trash2, Hash } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// ─── tiny helpers ─────────────────────────────────────────────────────────────
function uid() {
  return `_${Math.random().toString(36).slice(2, 9)}`;
}

// ─── Shared param options (used across multiple types) ───────────────────────
const PARAM_OPTIONS = {
  year: [{ value: "{{year}}", label: "True" }],
  month: [{ value: "{{month}}", label: "True" }],
  is_cfo: [
    { value: "true", label: "true" },
    { value: "false", label: "false" },
  ],
  currency: [
    { value: "Local Currency", label: "Local Currency" },
    { value: "Reporting Currency", label: "Reporting Currency" },
  ],
  kpi: [
    { value: "revenue", label: "Revenue" },
    { value: "occupancy", label: "Occupancy" },
    { value: "atv", label: "ATV" },
    { value: "als", label: "ALS" },
    { value: "on_street", label: "On Street" },
  ],
  device_onstreet: [
    { value: "JED", label: "JED – Jeddah On-Street" },
    { value: "MOC", label: "MOC – Ministry of Culture" },
    { value: "MAK", label: "MAK – Makkah" },
    { value: "KHA", label: "KHA – Khamis On-Street" },
    { value: "KFC", label: "KFC – Khamis Off-Street" },
    { value: "TAF", label: "TAF – Taif" },
    { value: "HAS", label: "HAS – Al Hassa" },
    { value: "JUB", label: "JUB – Jubail" },
    { value: "BHA", label: "BHA – Al Baha" },
    { value: "BLJ", label: "BLJ – Baljurashi" },
    { value: "DC", label: "DC – Digital City" },
    { value: "JV", label: "JV – Mawgif Jeddah JV" },
  ],
  value: [
    { value: "airport", label: "Airport" },
    { value: "on_street", label: "On Street" },
    { value: "off_street", label: "Off Street" },
  ],
};

// ─── Type-specific measure / value_type / version options ────────────────────
const TYPE_PARAM_OPTIONS = {
  main_table: {
    measure: [{ value: "0", label: "0" }],
  },
  third_table: {
    value_type: [
      { value: "2", label: "TOWS" },
      { value: "3", label: "CLAMPS" },
      { value: "4", label: "CNs" },
      { value: "5", label: "Parking Stats" },
      { value: "6", label: "Casual Parking Stats" },
    ],
  },
  large_table: {
    measure: [
      { value: "2", label: "Outturn Actual & Budget" },
      { value: "3", label: "Outturn Actual & Forecast" },
    ],
  },
  kpi_enforcement_table: {
    value_type: [{ value: "1", label: "1" }],
  },
  bar_chart: {
    version: [
      { value: "Actual", label: "Actual" },
      { value: "Budget", label: "Budget" },
      { value: "Forecast", label: "Forecast" },
      { value: "variance_to_budget", label: "Variance to Budget" },
      { value: "variance_to_forecast", label: "Variance to Forecast" },
    ],
    data_type: [
      { value: "excluding_fridays", label: "Excluding Fridays" },
      { value: "only_fridays", label: "Only Fridays" },
    ],
  },
  kpi_chart_12: {
    kpi: [{ value: "revenue", label: "Revenue" }],
  },
  kpi_chart_12_new: {
    value_type: [{ value: "1", label: "1" }],
  },
  new_chart_kpi: {
    kpi: [{ value: "on_street", label: "On Street" }],
  },
  kpi_chart_new: {
    value_type: [
      { value: "2", label: "Monthly Rental Income" },
      { value: "3", label: "Avg Rent Per Sq Mtrs" },
      { value: "4", label: "Rented Units Occupancy" },
    ],
  },
  new_stack_chart: {
    value_type: [
      { value: "1", label: "1" },
      { value: "2", label: "2" },
    ],
  },
  line_bars: {
    measure: [
      { value: "Average Value of CN", label: "Average Value of CN" },
      { value: "Net Count", label: "Net Count" },
      { value: "Average Jobs Per day", label: "Average Jobs Per Day" },
      { value: "EOs/Drivers Employed", label: "EOs/Drivers Employed" },
      { value: "EOs/Drivers Deployed", label: "EOs/Drivers Deployed" },
    ],
    data_type: [{ value: "three_years", label: "Three Years" }],
  },
  line_bars_table: {
    measure: [{ value: "% Deployed to Employed", label: "% Deployed to Employed" }],
    data_type: [{ value: "three_years", label: "Three Years" }],
  },
  donut_chart_18: {
    value_type: [
      { value: "1", label: "Payment by Split" },
      { value: "2", label: "Payment Split by Type & Source" },
      { value: "3", label: "Revenue Split by Payment Type & Source" },
      { value: "4", label: "Payment by Source and Type" },
    ],
  },
  area_chart: {
    value_type: [
      { value: "2", label: "Casual & Membership" },
      { value: "3", label: "Casual Parkers" },
      { value: "4", label: "Membership Parkers" },
      { value: "5", label: "Average Daily Transaction" },
      { value: "6", label: "Average Daily Revenue" },
    ],
    average_calculation: [
      { value: "full_month", label: "Full Month" },
      { value: "only_fri_off", label: "Friday Off" },
      { value: "fri_and_sat_off", label: "Friday & Saturday Off" },
    ],
  },
  kpi_chart_13: {
    kpi: [{ value: "occupancy", label: "Occupancy" }],
  },
  kpi_chart_14: {
    value_type: [{ value: "1", label: "1" }],
  },
  new_chart_component: {
    version: [{ value: "ticket_Count", label: "Ticket Count" }],
  },
  Dotted_Line_Chart: {
    measure: [{ value: "4", label: "4" }],
  },

  donut_chart: {
    value_type: [
      { value: "1", label: "Payment by Split" },
      { value: "2", label: "Payment Split by Type & Source" },
      { value: "3", label: "Revenue Split by Payment Type & Source" },
      { value: "4", label: "Payment by Source and Type" },
    ],
  },
};

// ─── Return dropdown options for a given param key + page type ────────────────
function getDropdownOptions(paramKey, pageType) {
  // Type-specific options take priority
  if (TYPE_PARAM_OPTIONS[pageType]?.[paramKey]) {
    return TYPE_PARAM_OPTIONS[pageType][paramKey];
  }
  // Shared options
  if (paramKey === "year") return PARAM_OPTIONS.year;
  if (paramKey === "month") return PARAM_OPTIONS.month;
  if (paramKey === "is_cfo") return PARAM_OPTIONS.is_cfo;
  if (paramKey === "currency") return PARAM_OPTIONS.currency;
  if (paramKey === "kpi") return PARAM_OPTIONS.kpi;
  if (paramKey === "device_onstreet") return PARAM_OPTIONS.device_onstreet;
  if (paramKey === "value") return PARAM_OPTIONS.value;
  // Free-text fallback
  return null;
}

// ─── InputDialog ──────────────────────────────────────────────────────────────
const InputDialog = ({ open, title, placeholder, onConfirm, onCancel }) => {
  const [value, setValue] = useState("");
  const handleConfirm = () => {
    if (value.trim()) {
      onConfirm(value.trim());
      setValue("");
    }
  };
  const handleCancel = () => {
    setValue("");
    onCancel();
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleCancel()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="text-sm">{title}</DialogTitle>
        </DialogHeader>
        <Input
          autoFocus
          className="text-xs h-8"
          placeholder={placeholder}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleConfirm();
            if (e.key === "Escape") handleCancel();
          }}
        />
        <DialogFooter className="gap-2">
          <Button variant="outline" size="sm" className="h-7 text-xs" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            size="sm"
            className="h-7 text-xs"
            onClick={handleConfirm}
            disabled={!value.trim()}
          >
            Add
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ─── StableFocusInput ─────────────────────────────────────────────────────────
export const StableFocusInput = ({
  value,
  onChange,
  className,
  placeholder,
  type = "text",
  autoFocus,
}) => {
  const ref = useRef(null);
  const focused = useRef(false);
  useEffect(() => {
    if (ref.current && !focused.current) ref.current.value = value ?? "";
  }, [value]);
  return (
    <input
      ref={ref}
      type={type}
      className={className}
      placeholder={placeholder}
      autoFocus={autoFocus}
      defaultValue={value ?? ""}
      onFocus={() => {
        focused.current = true;
      }}
      onBlur={() => {
        focused.current = false;
      }}
      onChange={(e) => onChange(e.target.value)}
    />
  );
};

// ─── Style constants ──────────────────────────────────────────────────────────
const INPUT_CLS =
  "w-full px-2.5 py-1.5 text-xs rounded-lg border bg-background focus:outline-none focus:ring-2 focus:ring-primary/20 transition-shadow";
const MONO_INPUT_CLS = INPUT_CLS + " font-mono";

// ─── Shared small components ──────────────────────────────────────────────────
const LabelInput = ({ label, value, onChange, placeholder = "", mono = false, type = "text" }) => (
  <div className="space-y-0.5">
    <label className="text-[10px] font-medium text-muted-foreground">{label}</label>
    <StableFocusInput
      type={type}
      className={mono ? MONO_INPUT_CLS : INPUT_CLS}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
    />
  </div>
);

export const ToggleRow = ({ label, checked, onChange }) => (
  <div className="flex items-center justify-between py-0.5">
    <label className="text-[10px] font-medium text-muted-foreground">{label}</label>
    <Switch checked={!!checked} onCheckedChange={onChange} />
  </div>
);

const ColorInput = ({ label, value, onChange }) => (
  <div className="space-y-0.5">
    <label className="text-[10px] font-medium text-muted-foreground">{label}</label>
    <div className="flex items-center gap-2">
      <div className="h-6 w-6 rounded-full overflow-hidden border cursor-pointer">
        <input
          type="color"
          value={value || "#000000"}
          onChange={(e) => onChange(e.target.value)}
          className="h-20 w-20 -translate-x-1 -translate-y-12 cursor-pointer"
        />
      </div>
      <StableFocusInput
        className={MONO_INPUT_CLS + " flex-1"}
        placeholder="#rrggbb"
        value={value}
        onChange={onChange}
      />
    </div>
  </div>
);

const Section = ({ title, children }) => (
  <Card className="shadow-sm">
    <CardHeader className="pb-1.5 pt-3 px-3">
      <CardTitle className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
        {title}
      </CardTitle>
    </CardHeader>
    <CardContent className="px-3 pb-3 space-y-2.5">{children}</CardContent>
  </Card>
);

export const TitleSubtitleSection = ({ page, onUpdate, idx }) => {
  const u = (field, val) => onUpdate(idx, field, val);
  return (
    <Card className="shadow-sm">
      <CardHeader className="pb-1.5 pt-3 px-3">
        <CardTitle className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
          Title & Subtitle
        </CardTitle>
      </CardHeader>
      <CardContent className="px-3 pb-3 space-y-2.5">
        <ToggleRow
          label="Show Title"
          checked={page.showTitle}
          onChange={(v) => u("showTitle", v)}
        />
        <ToggleRow
          label="Show Subtitle"
          checked={page.showSubtitle}
          onChange={(v) => u("showSubtitle", v)}
        />
        {page?.showTitle && (
          <div className="space-y-0.5">
            <label className="text-[10px] font-medium text-muted-foreground">Title</label>
            <StableFocusInput
              className={INPUT_CLS}
              placeholder="Enter page title"
              value={page.title || ""}
              onChange={(v) => u("title", v)}
            />
          </div>
        )}
        {page?.showSubtitle && (
          <div className="space-y-0.5">
            <label className="text-[10px] font-medium text-muted-foreground">Subtitle</label>
            <StableFocusInput
              className={INPUT_CLS}
              placeholder="Enter subtitle"
              value={page.subtitle || ""}
              onChange={(v) => u("subtitle", v)}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export const ChartLines = ({ page, onUpdate, idx, showLine_1 = true, showLine_2 = true }) => {
  const u = (field, val) => onUpdate(idx, field, val);
  return (
    <Card className="shadow-sm">
      <CardHeader className="pb-1.5 pt-3 px-3">
        <CardTitle className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
          In-Chart Content
        </CardTitle>
      </CardHeader>
      <CardContent className="px-3 pb-3 space-y-2.5">
        {showLine_1 && (
          <div className="space-y-0.5">
            <label className="text-[10px] font-medium text-muted-foreground">Line 1</label>
            <StableFocusInput
              className={INPUT_CLS}
              placeholder="Top label inside chart"
              value={page.in_chart_content_line_1 || ""}
              onChange={(v) => u("in_chart_content_line_1", v)}
            />
          </div>
        )}
        {showLine_2 && (
          <div className="space-y-0.5">
            <label className="text-[10px] font-medium text-muted-foreground">Line 2</label>
            <StableFocusInput
              className={INPUT_CLS}
              placeholder="Bottom label inside chart"
              value={page.in_chart_content_line_2 || ""}
              onChange={(v) => u("in_chart_content_line_2", v)}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ─── ParamField — renders dropdown OR free-text input ────────────────────────
const ParamField = ({ paramKey, value, pageType, onChange }) => {
  const options = getDropdownOptions(paramKey, pageType);

  if (options) {
    // Normalize value to string for Select matching
    const strVal = value === null || value === undefined ? "" : String(value);
    return (
      <div className="flex-1 min-w-0">
        <Select
          value={strVal}
          onValueChange={(v) => {
            // Coerce back to correct type
            if (v === "true") onChange(true);
            else if (v === "false") onChange(false);
            else if (v !== "" && !isNaN(Number(v)) && !String(v).startsWith("{{"))
              onChange(Number(v));
            else onChange(v);
          }}
        >
          <SelectTrigger className="h-7 text-xs font-mono w-full">
            <SelectValue placeholder="Select…" />
          </SelectTrigger>
          <SelectContent>
            {options.map((opt) => (
              <SelectItem key={opt.value} value={opt.value} className="text-xs font-mono">
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }

  // Free-text fallback
  return (
    <StableFocusInput
      className={MONO_INPUT_CLS + " flex-1"}
      value={value === null || value === undefined ? "" : String(value)}
      onChange={onChange}
    />
  );
};

// ─── API + Params section ─────────────────────────────────────────────────────
export const ApiParamsSection = ({ page, onUpdate, idx, hiddenKeys = [] }) => {
  const [dialog, setDialog] = useState(false);
  const params = page.params || {};

  const setParam = (key, val) => {
    onUpdate(idx, "params", { ...params, [key]: val });
  };

  const deleteParam = (key) => {
    const p = { ...params };
    delete p[key];
    onUpdate(idx, "params", p);
  };

  return (
    <Section title="API & Params">
      <InputDialog
        open={dialog}
        title="New Param"
        placeholder="Param key…"
        onConfirm={(k) => {
          onUpdate(idx, "params", { ...params, [k]: "" });
          setDialog(false);
        }}
        onCancel={() => setDialog(false)}
      />
      <LabelInput
        label="API Endpoint"
        mono
        value={page.api || ""}
        onChange={(v) => onUpdate(idx, "api", v)}
        placeholder="/boardpack/…"
      />
      {Object.entries(params)
        .filter(([key]) => key !== "year" && key !== "month" && !hiddenKeys.includes(key))
        .map(([key, val]) => (
          <div key={key} className="flex items-center gap-1.5">
            <span className="text-[10px] font-mono text-muted-foreground w-20 shrink-0 truncate">
              {key}
            </span>
            <ParamField
              paramKey={key}
              value={val}
              pageType={page.type}
              onChange={(v) => setParam(key, v)}
            />
            {/* <button onClick={() => deleteParam(key)} className="text-muted-foreground hover:text-red-500 shrink-0">
            <Trash2 className="w-3 h-3" />
          </button> */}
          </div>
        ))}
      {/* <Button variant="outline" size="sm" className="h-6 text-[10px] gap-1 px-2" onClick={() => setDialog(true)}>
        <Plus className="w-3 h-3" /> Add Param
      </Button> */}
    </Section>
  );
};

// ─── datasetsConfig editor ────────────────────────────────────────────────────
const DATA_KEY_OPTIONS = {
  bar: ["enforcement", "parking"],
  line: ["enforcementAvg", "parkingAvg"],
};

const DatasetsConfigEditor = ({ datasets, onChange, title = "Datasets Config" }) => {
  const add = () =>
    onChange([
      ...(datasets || []),
      { type: "bar", label: "", dataKey: "enforcement", color: "#5B9BD5" },
    ]);
  const update = (i, key, val) =>
    onChange(datasets.map((d, j) => (j === i ? { ...d, [key]: val } : d)));
  const remove = (i) => onChange(datasets.filter((_, j) => j !== i));

  const handleTypeChange = (i, newType) => {
    const defaultKey = DATA_KEY_OPTIONS[newType]?.[0] ?? "";
    onChange(
      datasets.map((d, j) =>
        j !== i ? d : { ...d, type: newType, dataKey: defaultKey, borderDash: undefined }
      )
    );
  };

  const handleDashToggle = (i, enabled) => {
    onChange(
      datasets.map((d, j) => (j !== i ? d : { ...d, borderDash: enabled ? [4, 4] : undefined }))
    );
  };

  return (
    <Section title={title}>
      {(datasets || []).length === 0 && (
        <p className="text-[10px] text-muted-foreground italic">None — click Add.</p>
      )}
      {(datasets || []).map((ds, i) => (
        <div key={i} className="border rounded-lg p-2.5 space-y-2 bg-muted/20">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-semibold text-muted-foreground">#{i + 1}</span>
            <button onClick={() => remove(i)} className="text-muted-foreground hover:text-red-500">
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {/* Type select */}
            <div className="space-y-0.5">
              <label className="text-[10px] font-medium text-muted-foreground">Type</label>
              <Select value={ds.type || "bar"} onValueChange={(v) => handleTypeChange(i, v)}>
                <SelectTrigger className="h-7 text-xs w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="bar" className="text-xs">
                    bar
                  </SelectItem>
                  <SelectItem value="line" className="text-xs">
                    line
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Label */}
            <LabelInput
              label="Label"
              value={ds.label || ""}
              onChange={(v) => update(i, "label", v)}
            />

            {/* DataKey select — options depend on type */}
            <div className="space-y-0.5">
              <label className="text-[10px] font-medium text-muted-foreground">Data Key</label>
              <Select value={ds.dataKey || ""} onValueChange={(v) => update(i, "dataKey", v)}>
                <SelectTrigger className="h-7 text-xs font-mono w-full">
                  <SelectValue placeholder="Select…" />
                </SelectTrigger>
                <SelectContent>
                  {(DATA_KEY_OPTIONS[ds.type] ?? []).map((k) => (
                    <SelectItem key={k} value={k} className="text-xs font-mono">
                      {k}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <ColorInput
              label="Color"
              value={ds.color || ""}
              onChange={(v) => update(i, "color", v)}
            />
          </div>

          {/* BorderDash toggle — only for line type */}
          {ds.type === "line" && (
            <div className="space-y-0.5">
              <label className="text-[10px] font-medium text-muted-foreground">Border Dash</label>
              <Select
                value={ds.borderDash ? "true" : "false"}
                onValueChange={(v) => handleDashToggle(i, v === "true")}
              >
                <SelectTrigger className="h-7 text-xs w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="true" className="text-xs">
                    Dashed
                  </SelectItem>
                  <SelectItem value="false" className="text-xs">
                    Solid
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      ))}
      <Button variant="outline" size="sm" className="h-6 text-[10px] gap-1 px-2" onClick={add}>
        <Plus className="w-3 h-3" /> Add Dataset
      </Button>
    </Section>
  );
};

// ─── tableConfig editor ───────────────────────────────────────────────────────
const TableConfigEditor = ({ tableConfig, onChange }) => {
  const add = () =>
    onChange([...(tableConfig || []), { label: "", dataKey: "", color: "#5B9BD5" }]);
  const update = (i, key, val) =>
    onChange(tableConfig.map((d, j) => (j === i ? { ...d, [key]: val } : d)));
  const remove = (i) => onChange(tableConfig.filter((_, j) => j !== i));
  return (
    <Section title="Table Config">
      {(tableConfig || []).length === 0 && (
        <p className="text-[10px] text-muted-foreground italic">None.</p>
      )}
      {(tableConfig || []).map((tc, i) => (
        <div key={i} className="border rounded-lg p-2.5 space-y-2 bg-muted/20">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-semibold text-muted-foreground">#{i + 1}</span>
            <button onClick={() => remove(i)} className="text-muted-foreground hover:text-red-500">
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <LabelInput
              label="Label"
              value={tc.label || ""}
              onChange={(v) => update(i, "label", v)}
            />
            {/* <LabelInput label="Data Key" mono value={tc.dataKey || ""} onChange={(v) => update(i, "dataKey", v)} /> */}
            <div className="space-y-0.5">
              <label className="text-[10px] font-medium text-muted-foreground">Type</label>
              <Select value={tc.dataKey || "bar"} onValueChange={(v) => update(i, "dataKey", v)}>
                <SelectTrigger className="h-7 text-xs w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="enforcement" className="text-xs">
                    Enforcement
                  </SelectItem>
                  <SelectItem value="parking" className="text-xs">
                    Parking
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <ColorInput
            label="Color"
            value={tc.color || ""}
            onChange={(v) => update(i, "color", v)}
          />
        </div>
      ))}
      <Button variant="outline" size="sm" className="h-6 text-[10px] gap-1 px-2" onClick={add}>
        <Plus className="w-3 h-3" /> Add Row
      </Button>
    </Section>
  );
};

// ─── LEGEND_CONFIG editor ─────────────────────────────────────────────────────
const LegendConfigEditor = ({ legendConfig, onChange }) => {
  const [dialog, setDialog] = useState(false);
  const entries = Object.entries(legendConfig || {});
  const update = (key, label) => onChange({ ...(legendConfig || {}), [key]: { label } });
  const remove = (key) => {
    const c = { ...(legendConfig || {}) };
    delete c[key];
    onChange(c);
  };
  return (
    <Section title="Legend Config">
      <InputDialog
        open={dialog}
        title="New Legend Key"
        placeholder="e.g. enforcement"
        onConfirm={(k) => {
          onChange({ ...(legendConfig || {}), [k]: { label: "" } });
          setDialog(false);
        }}
        onCancel={() => setDialog(false)}
      />
      {entries.length === 0 && <p className="text-[10px] text-muted-foreground italic">None.</p>}
      {entries.map(([key, val]) => (
        <div key={key} className="flex items-center gap-2">
          <span className="text-[10px] font-mono text-muted-foreground w-24 shrink-0 truncate">
            {key}
          </span>
          <StableFocusInput
            className={INPUT_CLS + " flex-1"}
            placeholder="Display label"
            value={val?.label || ""}
            onChange={(v) => update(key, v)}
          />
          <button
            onClick={() => remove(key)}
            className="text-muted-foreground hover:text-red-500 shrink-0"
          >
            <Trash2 className="w-3 h-3" />
          </button>
        </div>
      ))}
      <Button
        variant="outline"
        size="sm"
        className="h-6 text-[10px] gap-1 px-2"
        onClick={() => setDialog(true)}
      >
        <Plus className="w-3 h-3" /> Add Key
      </Button>
    </Section>
  );
};

// ─── Line datasets editor ─────────────────────────────────────────────────────
const LineDatasetEditor = ({ datasets, onChange, title = "Line Datasets" }) => {
  const add = () => onChange([...(datasets || []), { label: "", color: "#1787B0" }]);
  const update = (i, key, val) =>
    onChange(datasets.map((d, j) => (j === i ? { ...d, [key]: val } : d)));
  const remove = (i) => onChange(datasets.filter((_, j) => j !== i));
  return (
    <Section title={title}>
      {(datasets || []).length === 0 && (
        <p className="text-[10px] text-muted-foreground italic">None.</p>
      )}
      {(datasets || []).map((ds, i) => (
        <div key={i} className="border rounded-lg p-2.5 space-y-2 bg-muted/20">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-semibold text-muted-foreground">#{i + 1}</span>
            <button onClick={() => remove(i)} className="text-muted-foreground hover:text-red-500">
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <LabelInput
              label="Label"
              value={ds.label || ""}
              onChange={(v) => update(i, "label", v)}
            />
            <ColorInput
              label="Color"
              value={ds.color || ""}
              onChange={(v) => update(i, "color", v)}
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <LabelInput
              label="Order"
              type="number"
              value={ds.order ?? ""}
              onChange={(v) => update(i, "order", Number(v))}
            />
            <LabelInput
              label="Dash (e.g. 5,5)"
              mono
              value={(ds.borderDash || []).join(",")}
              onChange={(v) =>
                update(
                  i,
                  "borderDash",
                  v
                    ? v
                        .split(",")
                        .map(Number)
                        .filter((n) => !isNaN(n))
                    : undefined
                )
              }
            />
          </div>
        </div>
      ))}
      <Button variant="outline" size="sm" className="h-6 text-[10px] gap-1 px-2" onClick={add}>
        <Plus className="w-3 h-3" /> Add
      </Button>
    </Section>
  );
};

// ─── KPI colors/labels editors ────────────────────────────────────────────────
const KpiColorsEditor = ({ colors, onChange }) => {
  const keys = ["revenue", "transaction", "atv", "als"];
  return (
    <Section title="KPI Colors">
      <div className="grid grid-cols-2 gap-2">
        {keys.map((k) => (
          <ColorInput
            key={k}
            label={k}
            value={(colors || {})[k] || ""}
            onChange={(v) => onChange({ ...(colors || {}), [k]: v })}
          />
        ))}
      </div>
    </Section>
  );
};

const KpiLabelsEditor = ({ labels, onChange }) => {
  const keys = ["revenue", "transaction", "atv", "als"];
  return (
    <Section title="KPI Labels">
      <div className="space-y-2">
        {keys.map((k) => (
          <LabelInput
            key={k}
            label={k}
            value={(labels || {})[k] || ""}
            onChange={(v) => onChange({ ...(labels || {}), [k]: v })}
          />
        ))}
      </div>
    </Section>
  );
};

// ─── SubContractsEditor ───────────────────────────────────────────────────────
function parseSubContracts(raw) {
  return (raw || []).map((item) => {
    if (typeof item === "object" && item !== null && !Array.isArray(item)) {
      const children = (item.sub_contracts || []).map((c) => parseSubContracts([c])[0]);
      return {
        id: uid(),
        value: String(item.name || ""),
        nested: children.length > 0 ? children : [],
      };
    }
    if (Array.isArray(item)) {
      const [parent, ...children] = item;
      return {
        id: uid(),
        value: String(parent || ""),
        nested: children.map((c) => ({ id: uid(), value: String(c || ""), nested: [] })),
      };
    }
    return { id: uid(), value: String(item || ""), nested: [] };
  });
}

function serializeSubContracts(items) {
  return items
    .filter((item) => item.value.trim() !== "")
    .map((item) => {
      if (item.nested !== null && item.nested !== undefined) {
        const children = serializeSubContracts(item.nested);
        const obj = { name: item.value };
        if (children.length > 0) obj.sub_contracts = children;
        return obj;
      }
      return { name: item.value };
    });
}

export const SubContractsEditor = ({ subContracts, onChange }) => {
  const [items, setItems] = useState(() => parseSubContracts(subContracts));
  const ownRef = useRef(
    JSON.stringify(serializeSubContracts(parseSubContracts(subContracts || [])))
  );

  useEffect(() => {
    const incoming = JSON.stringify(subContracts);
    if (incoming !== ownRef.current) {
      ownRef.current = incoming;
      setItems(parseSubContracts(subContracts));
    }
  }, [subContracts]);

  const commit = useCallback(
    (next) => {
      setItems(next);
      const serialized = serializeSubContracts(next);
      ownRef.current = JSON.stringify(serialized);
      onChange(serialized.length > 0 ? serialized : undefined);
    },
    [onChange]
  );

  const addTop = () => commit([...items, { id: uid(), value: "", nested: [] }]);
  const updateValue = (i, val) =>
    commit(items.map((it, j) => (j === i ? { ...it, value: val } : it)));
  const removeItem = (i) => commit(items.filter((_, j) => j !== i));
  const addChild = (i) =>
    commit(
      items.map((it, j) =>
        j !== i
          ? it
          : { ...it, nested: [...(it.nested || []), { id: uid(), value: "", nested: null }] }
      )
    );
  const updateChild = (i, j, val) =>
    commit(
      items.map((it, ii) =>
        ii !== i
          ? it
          : { ...it, nested: it.nested.map((c, jj) => (jj === j ? { ...c, value: val } : c)) }
      )
    );
  const removeChild = (i, j) =>
    commit(
      items.map((it, ii) =>
        ii !== i ? it : { ...it, nested: it.nested.filter((_, jj) => jj !== j) }
      )
    );
  const toggleChildNested = (i, j) =>
    commit(
      items.map((it, ii) =>
        ii !== i
          ? it
          : {
              ...it,
              nested: it.nested.map((c, jj) =>
                jj !== j
                  ? c
                  : {
                      ...c,
                      nested: c.nested !== null ? null : [{ id: uid(), value: "", nested: null }],
                    }
              ),
            }
      )
    );
  const addGrandChild = (i, j) =>
    commit(
      items.map((it, ii) =>
        ii !== i
          ? it
          : {
              ...it,
              nested: it.nested.map((c, jj) =>
                jj !== j
                  ? c
                  : { ...c, nested: [...(c.nested || []), { id: uid(), value: "", nested: null }] }
              ),
            }
      )
    );
  const updateGrandChild = (i, j, k, val) =>
    commit(
      items.map((it, ii) =>
        ii !== i
          ? it
          : {
              ...it,
              nested: it.nested.map((c, jj) =>
                jj !== j
                  ? c
                  : {
                      ...c,
                      nested: c.nested.map((gc, kk) => (kk !== k ? gc : { ...gc, value: val })),
                    }
              ),
            }
      )
    );
  const removeGrandChild = (i, j, k) =>
    commit(
      items.map((it, ii) =>
        ii !== i
          ? it
          : {
              ...it,
              nested: it.nested.map((c, jj) =>
                jj !== j ? c : { ...c, nested: c.nested.filter((_, kk) => kk !== k) }
              ),
            }
      )
    );

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <label className="text-[10px] font-medium text-muted-foreground">Sub Contracts</label>
        <Button
          variant="outline"
          size="sm"
          className="h-5 text-[10px] gap-1 px-1.5"
          onClick={addTop}
        >
          <Plus className="w-2.5 h-2.5" /> Add
        </Button>
      </div>
      {items.length === 0 && <p className="text-[10px] text-muted-foreground italic">None.</p>}
      {items.map((item, i) => (
        <div key={item.id} className="border rounded-lg overflow-hidden bg-background">
          <div className="flex items-center gap-1.5 px-2 py-1.5 bg-muted/30">
            <Hash className="w-3 h-3 text-muted-foreground shrink-0" />
            <StableFocusInput
              className={INPUT_CLS + " flex-1"}
              placeholder="Contract name"
              value={item.value}
              onChange={(v) => updateValue(i, v)}
            />
            <button
              onClick={() => addChild(i)}
              className="text-[9px] px-1.5 py-0.5 rounded border font-semibold whitespace-nowrap transition-colors bg-muted text-muted-foreground border-border hover:bg-blue-50 hover:text-blue-700 hover:border-blue-200"
            >
              + Add nested
            </button>
            <button
              onClick={() => removeItem(i)}
              className="text-muted-foreground hover:text-red-500"
            >
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
          {item.nested.length > 0 && (
            <div className="px-2.5 pt-1 pb-2 space-y-1.5 border-t bg-background/60">
              {item.nested.map((child, j) => (
                <div key={child.id} className="pl-3 border-l-2 border-primary/20 space-y-1">
                  <div className="flex items-center gap-1.5">
                    <StableFocusInput
                      className={INPUT_CLS + " flex-1"}
                      placeholder="Nested name"
                      value={child.value}
                      onChange={(v) => updateChild(i, j, v)}
                    />
                    {/* <button
                      onClick={() => toggleChildNested(i, j)}
                      className={`text-[9px] px-1.5 py-0.5 rounded border font-semibold whitespace-nowrap transition-colors ${child.nested !== null
                        ? "bg-purple-100 text-purple-700 border-purple-200"
                        : "bg-muted text-muted-foreground border-border"
                        }`}
                    >
                      {child.nested !== null ? "Deep ✓" : "+ Add deep"}
                    </button> */}
                    <button
                      onClick={() => removeChild(i, j)}
                      className="text-muted-foreground hover:text-red-500"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                  {/* {child.nested !== null && (
                    <div className="pl-3 border-l-2 border-purple-200/70 space-y-1 pb-1">
                      {child.nested.length === 0 && (
                        <p className="text-[10px] text-muted-foreground italic">No deep nested.</p>
                      )}
                      {child.nested.map((gc, k) => (
                        <div key={gc.id} className="flex items-center gap-1.5">
                          <StableFocusInput
                            className={INPUT_CLS + " flex-1"}
                            placeholder="Deep nested name"
                            value={gc.value}
                            onChange={(v) => updateGrandChild(i, j, k, v)}
                          />
                          <button onClick={() => removeGrandChild(i, j, k)} className="text-muted-foreground hover:text-red-500">
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                      <button
                        onClick={() => addGrandChild(i, j)}
                        className="flex items-center gap-1 text-[10px] text-purple-600 hover:text-purple-800 mt-0.5"
                      >
                        <Plus className="w-3 h-3" /> Add deep nested
                      </button>
                    </div>
                  )} */}
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

// ─── Generic API + Params section (reusable with any apiKey/paramsKey) ───────
const ApiSectionGeneric = ({
  title = "API & Params",
  page,
  apiKey = "api",
  paramsKey = "params",
  onUpdate,
  idx,
}) => {
  const [dialog, setDialog] = useState(false);
  const params = page[paramsKey] || {};
  const setParam = (key, val) => onUpdate(idx, paramsKey, { ...params, [key]: val });
  const deleteParam = (key) => {
    const p = { ...params };
    delete p[key];
    onUpdate(idx, paramsKey, p);
  };
  return (
    <Section title={title}>
      <InputDialog
        open={dialog}
        title="New Param"
        placeholder="Param key…"
        onConfirm={(k) => {
          onUpdate(idx, paramsKey, { ...params, [k]: "" });
          setDialog(false);
        }}
        onCancel={() => setDialog(false)}
      />
      <LabelInput
        label="API Endpoint"
        mono
        value={page[apiKey] || ""}
        onChange={(v) => onUpdate(idx, apiKey, v)}
        placeholder="/boardpack/…"
      />
      {Object.entries(params)
        .filter(([key]) => key !== "year" && key !== "month")
        .map(([key, val]) => (
          <div key={key} className="flex items-center gap-1.5">
            <span className="text-[10px] font-mono text-muted-foreground w-20 shrink-0 truncate">
              {key}
            </span>
            <ParamField
              paramKey={key}
              value={val}
              pageType={page.type}
              onChange={(v) => setParam(key, v)}
            />
            <button
              onClick={() => deleteParam(key)}
              className="text-muted-foreground hover:text-red-500 shrink-0"
            >
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        ))}
      <Button
        variant="outline"
        size="sm"
        className="h-6 text-[10px] gap-1 px-2"
        onClick={() => setDialog(true)}
      >
        <Plus className="w-3 h-3" /> Add Param
      </Button>
    </Section>
  );
};

// ─── Data Rules editor (take_first, skip_first, take_next, take_last) ─────────
const DataRulesSection = ({ page, onUpdate, idx }) => {
  const rules = page.data_rules || {};
  const setRule = (key, val) => {
    const next = { ...rules };
    if (val === "" || val === null || val === undefined) {
      delete next[key];
    } else {
      next[key] = Number(val);
    }
    onUpdate(idx, "data_rules", Object.keys(next).length > 0 ? next : undefined);
  };
  return (
    <Section title="Data Rules">
      <div className="grid grid-cols-2 gap-2">
        <LabelInput
          label="Take First"
          type="number"
          value={rules.take_first_objects ?? ""}
          onChange={(v) => setRule("take_first_objects", v)}
          placeholder="—"
        />
        <LabelInput
          label="Skip First"
          type="number"
          value={rules.skip_first_objects ?? ""}
          onChange={(v) => setRule("skip_first_objects", v)}
          placeholder="—"
        />
        <LabelInput
          label="Take Next"
          type="number"
          value={rules.take_next_objects ?? ""}
          onChange={(v) => setRule("take_next_objects", v)}
          placeholder="—"
        />
        <LabelInput
          label="Take Last"
          type="number"
          value={rules.take_last_objects ?? ""}
          onChange={(v) => setRule("take_last_objects", v)}
          placeholder="—"
        />
      </div>
    </Section>
  );
};

// ─── Donut Chart Config editor (first_chart / second_chart) ──────────────────
const DONUT_KEY_OPTIONS = [
  { value: "cash", label: "CASH" },
  { value: "card", label: "CARD" },
  { value: "pos", label: "POS" },
  { value: "apm", label: "APM" },
  { value: "appWeb", label: "APP/WEB" },
  { value: "verifier", label: "VERIFIER" },
  { value: "cashPos", label: "Cash POS" },
  { value: "cashApm", label: "Cash APM" },
  { value: "cardPos", label: "Card POS" },
  { value: "cardApm", label: "Card APM" },
  { value: "cardAppWeb", label: "Card APP/WEB" },
  { value: "cardVerifier", label: "Card VERIFIER" },
  { value: "apmCash", label: "apmCash — CASH" },
  { value: "apmCard", label: "apmCard — CARD" },
  { value: "posCash", label: "posCash — CASH" },
  { value: "posCard", label: "posCard — CARD" },
  { value: "pnd", label: "pnd — PND" },
  { value: "pndAppWeb", label: "pndAppWeb — APP/WEB" },
  { value: "PND :: CARD", label: "PND :: CARD" },
  { value: "PND :: COINS", label: "PND :: COINS" },
  { value: "PND :: EWALLET", label: "PND :: EWALLET" },
  { value: "PND :: NOTES", label: "PND :: NOTES" },
];

const DonutChartSubEditor = ({ chartKey, config, onChange }) => {
  const keys = config?.keys || [];
  const colors = config?.colors || {};

  const addKey = (k) => {
    if (!k || keys.includes(k)) return;
    onChange({ ...config, keys: [...keys, k], colors: { ...colors, [k]: "#1D8AE9" } });
  };
  const removeKey = (k) => {
    const newColors = { ...colors };
    delete newColors[k];
    onChange({ keys: keys.filter((x) => x !== k), colors: newColors });
  };
  const updateColor = (k, color) => onChange({ ...config, colors: { ...colors, [k]: color } });

  const available = DONUT_KEY_OPTIONS.filter((o) => !keys.includes(o.value));

  const changeKey = (oldKey, newKey) => {
    if (!newKey || newKey === oldKey || keys.includes(newKey)) return;
    const newKeys = keys.map((k) => (k === oldKey ? newKey : k));
    const newColors = {};
    newKeys.forEach((k) => {
      newColors[k] = k === newKey ? colors[oldKey] || "#1D8AE9" : colors[k];
    });
    onChange({ keys: newKeys, colors: newColors });
  };

  return (
    <div className="space-y-2">
      {keys.length === 0 && <p className="text-[10px] text-muted-foreground italic">No keys.</p>}
      {keys.map((k) => (
        <div key={k} className="rounded-md border bg-background p-2 space-y-1.5">
          {/* Key selector row */}
          <div className="flex items-center gap-2">
            <Select value={k} onValueChange={(v) => changeKey(k, v)}>
              <SelectTrigger className="h-6 text-[10px] px-2 flex-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {/* current key always shown */}
                <SelectItem key={k} value={k} className="text-[10px]">
                  {DONUT_KEY_OPTIONS.find((o) => o.value === k)?.label || k}
                </SelectItem>
                {/* other available keys */}
                {available.map((o) => (
                  <SelectItem key={o.value} value={o.value} className="text-[10px]">
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <button
              onClick={() => removeKey(k)}
              className="text-muted-foreground hover:text-red-500 shrink-0"
            >
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
          {/* Color row */}
          <div className="flex items-center gap-2 pl-0.5">
            <div className="h-5 w-5 rounded-full overflow-hidden border cursor-pointer shrink-0">
              <input
                type="color"
                value={colors[k] || "#1D8AE9"}
                onChange={(e) => updateColor(k, e.target.value)}
                className="h-16 w-16 -translate-x-1 -translate-y-9 cursor-pointer"
              />
            </div>
            <StableFocusInput
              className={MONO_INPUT_CLS + " !w-28 flex-none"}
              placeholder="#rrggbb"
              value={colors[k] || ""}
              onChange={(v) => updateColor(k, v)}
            />
          </div>
        </div>
      ))}
      {available.length > 0 && (
        <Select onValueChange={(v) => addKey(v)}>
          <SelectTrigger className="h-7 text-[10px] px-2 border-dashed text-muted-foreground">
            <SelectValue placeholder="＋ Add key…" />
          </SelectTrigger>
          <SelectContent>
            {available.map((o) => (
              <SelectItem key={o.value} value={o.value} className="text-[10px]">
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </div>
  );
};

const DONUT_PRESETS = {
  1: {
    first_chart: {
      keys: ["cash", "card"],
      colors: { cash: "#FF8A3C", card: "#48A5F6" },
    },
    second_chart: {
      keys: ["pos", "apm", "appWeb", "verifier"],
      colors: { pos: "#48A5F6", apm: "#2E7ABF", appWeb: "#c9611a", verifier: "#FF9044" },
    },
  },
  2: {
    first_chart: {
      keys: ["cashPos", "cashApm"],
      colors: { cashPos: "#FF8A3C", cashApm: "#48A5F6" },
    },
    second_chart: {
      keys: ["cardPos", "cardApm", "cardAppWeb", "cardVerifier"],
      colors: {
        cardPos: "#48A5F6",
        cardApm: "#2E7ABF",
        cardAppWeb: "#c9611a",
        cardVerifier: "#FF9044",
      },
    },
  },
  3: {
    first_chart: {
      keys: ["apmCash", "apmCard"],
      colors: { apmCash: "#FF8A3C", apmCard: "#48A5F6" },
    },
    second_chart: {
      keys: ["posCash", "posCard"],
      colors: { posCash: "#FF8A3C", posCard: "#48A5F6" },
    },
  },
  4: {
    first_chart: {
      keys: ["pnd", "pndAppWeb"],
      colors: { pnd: "#FF8A3C", pndAppWeb: "#c9611a" },
    },
    second_chart: {
      keys: ["PND :: CARD", "PND :: COINS", "PND :: EWALLET", "PND :: NOTES"],
      colors: {
        "PND :: CARD": "#48A5F6",
        "PND :: COINS": "#2E7ABF",
        "PND :: EWALLET": "#FF9044",
        "PND :: NOTES": "#FF8A3C",
      },
    },
  },
};

const DonutChartConfigEditor = ({ chartConfig, onChange, valueType }) => {
  const cfg = chartConfig || {};

  return (
    <Section title="Chart Config">
      <div className="space-y-4">
        <div className="rounded-lg border bg-muted/20 p-3 space-y-2">
          <p className="text-[10px] font-semibold text-foreground uppercase tracking-wide">
            First Chart
          </p>
          <DonutChartSubEditor
            chartKey="first_chart"
            config={cfg.first_chart || { keys: [], colors: {} }}
            onChange={(v) => onChange({ ...cfg, first_chart: v })}
          />
        </div>
        <div className="rounded-lg border bg-muted/20 p-3 space-y-2">
          <p className="text-[10px] font-semibold text-foreground uppercase tracking-wide">
            Second Chart
          </p>
          <DonutChartSubEditor
            chartKey="second_chart"
            config={cfg.second_chart || { keys: [], colors: {} }}
            onChange={(v) => onChange({ ...cfg, second_chart: v })}
          />
        </div>
      </div>
    </Section>
  );
};

// ─── Profile Page fields (shared by all profile page types) ───────────────────
const ProfilePageFields = ({ page, idx, onUpdate }) => {
  const hasApi2 = page.api2 !== undefined || page.params2 !== undefined;
  return (
    <>
      <Section title="Page Settings">
        <LabelInput
          label="Title"
          value={page.title || ""}
          onChange={(v) => onUpdate(idx, "title", v)}
        />
        <div className="bg-muted/30 rounded-lg px-2.5 py-1">
          <ToggleRow
            label="Has Secondary API"
            checked={hasApi2}
            onChange={(enabled) => {
              if (enabled) {
                onUpdate(idx, "api2", "/boardpack/parking/bays");
              } else {
                onUpdate(idx, "api2", undefined);
                onUpdate(idx, "params2", undefined);
              }
            }}
          />
        </div>
      </Section>
      <ApiSectionGeneric
        title="Primary API & Params"
        page={page}
        apiKey="api"
        paramsKey="params"
        onUpdate={onUpdate}
        idx={idx}
      />
      {hasApi2 && (
        <ApiSectionGeneric
          title="Secondary API & Params"
          page={page}
          apiKey="api2"
          paramsKey="params2"
          onUpdate={onUpdate}
          idx={idx}
        />
      )}
      <DataRulesSection page={page} onUpdate={onUpdate} idx={idx} />
    </>
  );
};

// ─── RamadanRow ───────────────────────────────────────────────────────────────
const RamadanRow = ({ page, onUpdate, idx }) => {
  const u = (field, val) => onUpdate(idx, field, val);
  return (
    <Section title="Ramadan Row">
      <div className="bg-muted/30 rounded-lg px-2.5 py-1">
        <ToggleRow
          label="Show Ramadan Row"
          checked={page.showRamadanRow}
          onChange={(v) => u("showRamadanRow", v)}
        />
      </div>
      {page.showRamadanRow && (
        <ColorInput
          label="Ramadan Border Color"
          value={page.ramadanBorderColor || ""}
          onChange={(v) => u("ramadanBorderColor", v)}
        />
      )}
    </Section>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// ─── MAIN: TypeSpecificFields ─────────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════════════════
export const TypeSpecificFields = ({ page, idx, onUpdate }) => {
  const u = (field, val) => onUpdate(idx, field, val);

  switch (page.type) {
    case "title_page":
      return (
        <Section title="Page Config">
          <LabelInput
            label="Contract Label"
            value={page.contract || ""}
            onChange={(v) => u("contract", v)}
            placeholder="e.g. Jeddah Airport"
          />
          <SubContractsEditor
            subContracts={page.sub_contracts || []}
            onChange={(v) => u("sub_contracts", v)}
          />
          <div className="bg-muted/30 rounded-lg px-2.5 py-1 space-y-0.5">
            <ToggleRow
              label="Show Date"
              checked={page.showDate}
              onChange={(v) => u("showDate", v)}
            />
            <ToggleRow
              label="Show Note"
              checked={page.showNote}
              onChange={(v) => u("showNote", v)}
            />
          </div>
          {page.showNote && (
            <div className="space-y-0.5">
              <label className="text-[10px] font-medium text-muted-foreground">Note Text</label>
              <textarea
                className={INPUT_CLS + " min-h-14 resize-y"}
                value={page.notes || ""}
                onChange={(e) => u("notes", e.target.value)}
              />
            </div>
          )}
        </Section>
      );

    case "last_title":
      return <></>;

    case "main_table":
      return (
        <>
          <TitleSubtitleSection page={page} onUpdate={onUpdate} idx={idx} />
          <ApiParamsSection page={page} onUpdate={onUpdate} idx={idx} hiddenKeys={["measure"]} />
        </>
      );

    case "large_table":
      return (
        <>
          <TitleSubtitleSection page={page} onUpdate={onUpdate} idx={idx} />
          <ApiParamsSection page={page} onUpdate={onUpdate} idx={idx} />
        </>
      );

    case "kpi_enforcement_table":
      return (
        <>
          <TitleSubtitleSection page={page} onUpdate={onUpdate} idx={idx} />
          <ApiParamsSection page={page} onUpdate={onUpdate} idx={idx} hiddenKeys={["value_type"]} />
        </>
      );

    case "third_table":
      return (
        <>
          <TitleSubtitleSection page={page} onUpdate={onUpdate} idx={idx} />
          <Section title="Third Table Headers & Titles">
            <div className="grid grid-cols-1 gap-3">
              <LabelInput
                label="First Heading"
                value={page.first_heading || ""}
                onChange={(v) => u("first_heading", v)}
                placeholder="e.g. Jeddah Airport"
              />
              <LabelInput
                label="Second Heading (Bold)"
                value={page.second_heading_Bold || ""}
                onChange={(v) => u("second_heading_Bold", v)}
                placeholder="e.g. Enforcement Headline Operational Stats..."
              />
              <LabelInput
                label="Third Heading"
                value={page.third_hedaing || ""}
                onChange={(v) => u("third_hedaing", v)}
                placeholder="e.g. TOWS / CLAMPS / CNs"
              />
              <LabelInput
                label="Header Title"
                value={page.headerTitle || ""}
                onChange={(v) => u("headerTitle", v)}
                placeholder="e.g. JEDDAH AIRPORT"
              />
              <LabelInput
                label="Subheader Title"
                value={page.subheaderTitle || ""}
                onChange={(v) => u("subheaderTitle", v)}
                placeholder="e.g. TOWS / Parking Stats"
              />
            </div>
          </Section>
          <ApiParamsSection page={page} onUpdate={onUpdate} idx={idx} />
        </>
      );

    case "bar_chart":
      return (
        <>
          <TitleSubtitleSection page={page} onUpdate={onUpdate} idx={idx} />
          <ApiParamsSection page={page} onUpdate={onUpdate} idx={idx} />
          <ChartLines page={page} onUpdate={onUpdate} idx={idx} showLine_2={false} />
          <LegendConfigEditor
            legendConfig={page.LEGEND_CONFIG || {}}
            onChange={(v) => u("LEGEND_CONFIG", v)}
          />
          <DatasetsConfigEditor
            datasets={page.datasetsConfig || []}
            onChange={(v) => u("datasetsConfig", v)}
            title="Datasets Config"
          />
          <TableConfigEditor
            tableConfig={page.tableConfig || []}
            onChange={(v) => u("tableConfig", v)}
          />
        </>
      );

    case "kpi_chart_12":
    case "kpi_chart_12_new":
    case "new_chart_kpi":
      return (
        <>
          <TitleSubtitleSection page={page} onUpdate={onUpdate} idx={idx} />
          <ApiParamsSection page={page} onUpdate={onUpdate} idx={idx} hiddenKeys={["kpi","value_type"]} />
          <ChartLines page={page} onUpdate={onUpdate} idx={idx} showLine_2={false} />
          <Section title="Display Options">
            <div className="bg-muted/30 rounded-lg px-2.5 py-1">
              <ToggleRow
                label="Show Data Labels"
                checked={page.showDataLabels}
                onChange={(v) => u("showDataLabels", v)}
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-0.5">
                <label className="text-[10px] font-medium text-muted-foreground">
                  Left Y-Axis Formatter
                </label>
                <div className="px-2.5 py-1 bg-muted/30 rounded-lg">
                  <ToggleRow
                    label="Enable"
                    checked={page.yAxisFormatters?.left}
                    onChange={(v) =>
                      u("yAxisFormatters", { ...(page.yAxisFormatters || {}), left: v })
                    }
                  />
                </div>
              </div>
              <div className="space-y-0.5">
                <label className="text-[10px] font-medium text-muted-foreground">
                  Right Y-Axis Formatter
                </label>
                <div className="px-2.5 py-1 bg-muted/30 rounded-lg">
                  <ToggleRow
                    label="Enable"
                    checked={page.yAxisFormatters?.right}
                    onChange={(v) =>
                      u("yAxisFormatters", { ...(page.yAxisFormatters || {}), right: v })
                    }
                  />
                </div>
              </div>
            </div>
          </Section>
          <KpiColorsEditor colors={page.colors} onChange={(v) => u("colors", v)} />
          <KpiLabelsEditor labels={page.labels} onChange={(v) => u("labels", v)} />
        </>
      );

    case "kpi_chart_new":
      return (
        <>
          <TitleSubtitleSection page={page} onUpdate={onUpdate} idx={idx} />
          <ChartLines page={page} onUpdate={onUpdate} idx={idx} />
          <ApiParamsSection page={page} onUpdate={onUpdate} idx={idx} />
        </>
      );

    case "new_stack_chart":
      return (
        <>
          <TitleSubtitleSection page={page} onUpdate={onUpdate} idx={idx} />
          <ChartLines page={page} onUpdate={onUpdate} idx={idx} showLine_2={false} />
          <ApiParamsSection page={page} onUpdate={onUpdate} idx={idx} />
          <Section title="Axis Labels">
            <LabelInput
              label="Bottom Label"
              value={page.bottomLabel || ""}
              onChange={(v) => u("bottomLabel", v)}
            />
            <LabelInput
              label="Y-Axis Label"
              value={page.yAxisLabel || ""}
              onChange={(v) => u("yAxisLabel", v)}
            />
          </Section>
          <Section title="Display Options">
            <div className="bg-muted/30 rounded-lg px-2.5 py-1">
              <ToggleRow
                label="Show Data Labels"
                checked={page.showDataLabels}
                onChange={(v) => u("showDataLabels", v)}
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-0.5">
                <label className="text-[10px] font-medium text-muted-foreground">
                  Left Y-Axis Formatter
                </label>
                <div className="px-2.5 py-1 bg-muted/30 rounded-lg">
                  <ToggleRow
                    label="Enable"
                    checked={page.yAxisFormatters?.left}
                    onChange={(v) =>
                      u("yAxisFormatters", { ...(page.yAxisFormatters || {}), left: v })
                    }
                  />
                </div>
              </div>
              <div className="space-y-0.5">
                <label className="text-[10px] font-medium text-muted-foreground">
                  Right Y-Axis Formatter
                </label>
                <div className="px-2.5 py-1 bg-muted/30 rounded-lg">
                  <ToggleRow
                    label="Enable"
                    checked={page.yAxisFormatters?.right}
                    onChange={(v) =>
                      u("yAxisFormatters", { ...(page.yAxisFormatters || {}), right: v })
                    }
                  />
                </div>
              </div>
            </div>
          </Section>
          <KpiColorsEditor colors={page.colors} onChange={(v) => u("colors", v)} />
          <KpiLabelsEditor labels={page.labels} onChange={(v) => u("labels", v)} />
        </>
      );
    case "line_bars":
    case "line_bars_table":
      return (
        <>
          <TitleSubtitleSection page={page} onUpdate={onUpdate} idx={idx} />
          <ChartLines page={page} onUpdate={onUpdate} idx={idx} showLine_2={false} />
          <ApiParamsSection page={page} onUpdate={onUpdate} idx={idx} />
          <LineDatasetEditor
            datasets={page.datasets || []}
            onChange={(v) => u("datasets", v)}
            title="Line Datasets"
          />
          <LineDatasetEditor
            datasets={page.barDatasets || []}
            onChange={(v) => u("barDatasets", v)}
            title="Bar Datasets"
          />
          {page.type === "line_bars_table" && (
            <>
              <RamadanRow page={page} onUpdate={onUpdate} idx={idx} />
              <Section title="Table Options">
                <div className="bg-muted/30 rounded-lg px-2.5 py-1">
                  <ToggleRow
                    label="Show Second Table"
                    checked={page.showSecondTable}
                    onChange={(v) => u("showSecondTable", v)}
                  />
                </div>
                {page.showSecondTable && (
                  <LabelInput
                    label="Second Table Data Key"
                    mono
                    value={page.secondTableData || ""}
                    onChange={(v) => u("secondTableData", v)}
                    placeholder="chartData.secondTableData"
                  />
                )}
              </Section>
            </>
          )}
        </>
      );

    case "donut_chart_18":
      return (
        <>
          <TitleSubtitleSection page={page} onUpdate={onUpdate} idx={idx} />
          <ChartLines page={page} onUpdate={onUpdate} idx={idx} />
          <ApiParamsSection page={page} onUpdate={onUpdate} idx={idx} />
        </>
      );

    case "area_chart":
      return (
        <>
          <TitleSubtitleSection page={page} onUpdate={onUpdate} idx={idx} />
          <ApiParamsSection page={page} onUpdate={onUpdate} idx={idx} />
          <Section title="Axis Labels">
            <LabelInput
              label="Y-Axis Label"
              value={page.yAxisLabel || ""}
              onChange={(v) => u("yAxisLabel", v)}
            />
          </Section>
        </>
      );

    case "kpi_chart_13":
      return (
        <>
          <TitleSubtitleSection page={page} onUpdate={onUpdate} idx={idx} />
          <ChartLines page={page} onUpdate={onUpdate} idx={idx} showLine_2={false} />
          <ApiParamsSection page={page} onUpdate={onUpdate} idx={idx} hiddenKeys={["kpi"]} />
        </>
      );
    case "kpi_chart_14":
      return (
        <>
          <TitleSubtitleSection page={page} onUpdate={onUpdate} idx={idx} />
          <ChartLines page={page} onUpdate={onUpdate} idx={idx} />
          <Section title="Axis Labels">
            <LabelInput
              label="Bottom Label"
              value={page.bottomLabel || ""}
              onChange={(v) => u("bottomLabel", v)}
            />
            <LabelInput
              label="Y-Axis Label"
              value={page.yAxisLabel || ""}
              onChange={(v) => u("yAxisLabel", v)}
            />
          </Section>
          <ApiParamsSection page={page} onUpdate={onUpdate} idx={idx} hiddenKeys={["value_type"]} />
        </>
      );

    case "new_chart_component":
      return (
        <>
          <TitleSubtitleSection page={page} onUpdate={onUpdate} idx={idx} />
          <ChartLines page={page} onUpdate={onUpdate} idx={idx} showLine_2={false} />
          <Section title="Axis Labels">
            <LabelInput
              label="Bottom Label"
              value={page.bottomLabel || ""}
              onChange={(v) => u("bottomLabel", v)}
            />
            <LabelInput
              label="Y-Axis Label"
              value={page.yAxisLabel || ""}
              onChange={(v) => u("yAxisLabel", v)}
            />
          </Section>
          <ApiParamsSection page={page} onUpdate={onUpdate} idx={idx} hiddenKeys={["version"]}/>
        </>
      );
    case "Dotted_Line_Chart":
      return (
        <>
          <TitleSubtitleSection page={page} onUpdate={onUpdate} idx={idx} />
          <ChartLines page={page} onUpdate={onUpdate} idx={idx} showLine_2={false} />
          <ApiParamsSection page={page} onUpdate={onUpdate} idx={idx} hiddenKeys={["measure"]} />
        </>
      );

    // case "area_chart":
    //   return (
    //     <>
    //       <TitleSubtitleSection page={page} onUpdate={onUpdate} idx={idx} />
    //       <Section title="Chart Settings">
    //         <LabelInput label="Y-Axis Label" value={page.yAxisLabel || ""} onChange={(v) => u("yAxisLabel", v)} />
    //       </Section>
    //       <RamadanRow />
    //       <ApiParamsSection page={page} onUpdate={onUpdate} idx={idx} />
    //     </>
    //   );

    // case "donut_chart_18":
    //   return (
    //     <>
    //       <TitleSubtitleSection page={page} onUpdate={onUpdate} idx={idx} />
    //       <ChartLines page={page} onUpdate={onUpdate} idx={idx} />
    //       <ApiParamsSection page={page} onUpdate={onUpdate} idx={idx} />
    //     </>
    //   );

    case "donut_chart":
      return (
        <>
          <TitleSubtitleSection page={page} onUpdate={onUpdate} idx={idx} />
          <ChartLines page={page} onUpdate={onUpdate} idx={idx} />
          <ApiParamsSection page={page} onUpdate={onUpdate} idx={idx} />
          <DonutChartConfigEditor
            chartConfig={page.chart_config || {}}
            onChange={(v) => u("chart_config", v)}
            valueType={page.params?.value_type}
          />
        </>
      );

    case "jeddah_profile_summary02":
    case "jeddah_profile_summary10":
    case "jeddah_profile_summary_arabic":
    case "jeddah_on_street_profile":
    case "jeddah_off_street_profile":
    case "sharjha_profile_table":
    case "profile_summary_airport_jordan":
    case "profile_summary_lovers_market":
    case "profile_summary_AlBatha_4":
    case "profile_summary_AlBatha_11":
    case "profile_summary_dammam":
    case "profile_summary_bilal":
    case "profile_summary_BabAlRee_4":
    case "profile_summary_BabAlRee_11":
    case "Gazzaz_4":
    case "Gazzaz_11":
    case "JedWatch_4":
    case "JedWatch_11":
    case "total_dubai_airport_profile":
    case "dubai_airport_t1_profile":
    case "dubai_airport_t2_profile":
    case "dubai_airport_t3_profile":
    case "dubai_airport_cargo_profile":
    case "madinah_haram_profile":
    case "madinah_haram_parking_profile":
    case "madinah_haram_enforcement_profile":
    case "taif_on_street_profile":
    case "taif_off_street_profile":
    case "khamis_on_street_profile":
    case "khamis_off_street_profile":
    case "jubail_on_street_profile":
    case "alhassa_on_street_profile":
    case "mawgif_jeddah_on_street_profile":
    case "digital_city_on_street_profile":
    case "digital_city_off_street_profile":
    case "albaha_on_street_profile":
    case "albaha_off_street_profile":
    case "dammam_causeway_on_street_profile":
    case "dammam_causeway_off_street_profile":
    case "baljurashi_on_street_profile":
    case "baljurashi_off_street_profile":
    case "makkah_on_street_profile":
    case "makkah_off_street_profile":
    case "makkah_misfalah_profile":
    case "makkah_jarwal_profile":
    case "moc_on_street_profile":
      return <ProfilePageFields page={page} idx={idx} onUpdate={onUpdate} />;

    default:
      return (
        <Section title="Settings">
          <p className="text-[10px] text-muted-foreground italic">
            No specific settings for this page type.
          </p>
        </Section>
      );
  }
};

export default TypeSpecificFields;
